import {
  useQuery,
  useMutation,
  QueryFunctionContext,
  UseQueryOptions,
  useQueries,
  UseQueryResult,
  UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import axios, { AxiosRequestConfig } from "axios";
import { useAuth } from "@/services/auth/AuthContext";
import getApiEndpoint from "@/services/api/endpoint";
import { buildApiUrl } from "@/services/api/baseUrl";
import { useMemo, useRef } from "react";
import { EmptyFilters, Filters } from "./types";
import { collapseNoValueFilters } from "@/services/api/noValueFilters";

export interface UseAPIOptions extends AxiosRequestConfig {
  pathParams?: Record<string, string>;
  queryParams?: Record<string, string>;
}

export interface QueryKeyArray extends Array<any> {
  0: string; // endpoint
  1?: UseAPIOptions; // options
  // Extra cache scope. Never read when building the request — it exists only
  // to keep responses that differ by it in separate cache entries.
  2?: { computed_fields?: any; tenantId?: string | null };
}

const TOKEN_EXPIRED_ERROR = "TOKEN_EXPIRED";
const UNAUTHORIZED_ERROR = "UNAUTHORIZED";

interface APIAuthData {
  token?: string | null;
  tenantId: string | null;
  tokenExpiry: number;
}

interface APIConfig {
  endpoint: string;
  options?: UseAPIOptions;
  queryOptions?: Partial<UseQueryOptions<any, any, any, QueryKeyArray>> & {
    computed_fields?: any;
  };
}

export const useAPIHelper = async (
  apiAuthData: APIAuthData,
  queryKey: QueryKeyArray,
  signal?: AbortSignal
): Promise<any> => {
  const { token, tenantId, tokenExpiry } = apiAuthData;
  const [endpoint, options] = queryKey;

  const apiCall = getApiEndpoint(endpoint, {
    pathParams: options?.pathParams,
    queryParams: options?.queryParams
  });

  const url = buildApiUrl(apiCall);

  if (tokenExpiry < Date.now()) {
    throw new Error(TOKEN_EXPIRED_ERROR);
  }

  try {
    const response = await axios({
      url: url,
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenantId,
        "ngrok-skip-browser-warning": "69420", // Avoid ngrok warning message
        ...options?.headers
      },
      ...options,
      signal
    });
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        // Handle 401 Unauthorized error. The account it was refused for rides
        // along so a refusal in a linked account can be told apart from an
        // expired session.
        const unauthorized: UnauthorizedError = new Error(UNAUTHORIZED_ERROR);
        unauthorized.tenantId = tenantId;
        throw unauthorized;
      }
    }
    throw error; // Re-throw other errors
  }
};

export const handleAuthRetry = (failureCount: number, error: any): boolean => {
  if (
    error?.message === TOKEN_EXPIRED_ERROR ||
    error?.message === UNAUTHORIZED_ERROR
  ) {
    if (failureCount <= 1) {
      return true;
    }
  }
  return false;
};

// Frontegg's redirect login re-authenticates silently while the provider session is still
// alive, so each call mints a fresh token rather than navigating away. Expiry is detected
// locally, before any network call, so every mounted query fails in the same tick and would
// otherwise ask for a token of its own. One busy screen is enough to turn a single expiry
// into twenty token requests inside four seconds, which saturates the permissions service
// and gets the logins denied outright.
//
// A time window rather than a one-shot latch: if the re-authentication itself fails, the app
// has to be able to ask again instead of sitting unauthenticated forever.
const LOGIN_REQUEST_WINDOW_MS = 10_000;
let lastLoginRequestAt = 0;

export const requestLogin = (login: () => void): void => {
  const now = Date.now();
  if (now - lastLoginRequestAt < LOGIN_REQUEST_WINDOW_MS) {
    return;
  }
  lastLoginRequestAt = now;
  login();
};

// Module state outlives a test file, so every suite touching requestLogin must clear it.
export const resetLoginRequestWindow = (): void => {
  lastLoginRequestAt = 0;
};

type UnauthorizedError = Error & { tenantId?: string | null };

// Set while the app can act in an account other than the one the user signed
// in to. Given the account a 401 was refused for, it returns true when it took
// care of the refusal, so no re-login is requested for it.
type LinkedAccountRefusalHandler = (tenantId: string) => boolean;
let linkedAccountRefusalHandler: LinkedAccountRefusalHandler | null = null;

export const setLinkedAccountRefusalHandler = (
  handler: LinkedAccountRefusalHandler | null
): void => {
  linkedAccountRefusalHandler = handler;
};

export const isHandledLinkedAccountRefusal = (error: unknown): boolean => {
  const refusal = error as UnauthorizedError | undefined;
  const tenantId = refusal?.tenantId;
  return (
    refusal?.message === UNAUTHORIZED_ERROR &&
    typeof tenantId === "string" &&
    linkedAccountRefusalHandler !== null &&
    linkedAccountRefusalHandler(tenantId)
  );
};

export const createAuthRetryHandler = (
  login: () => void
): ((failureCount: number, error: any) => boolean) => {
  return (failureCount: number, error: any): boolean => {
    if (isHandledLinkedAccountRefusal(error)) {
      return false;
    }
    if (
      error?.message === TOKEN_EXPIRED_ERROR ||
      error?.message === UNAUTHORIZED_ERROR
    ) {
      if (failureCount <= 1) {
        return true;
      }
      requestLogin(login);
      return false;
    }
    return false;
  };
};

const useAPI = (
  endpoint: string,
  options?: UseAPIOptions,
  queryOptions?: Partial<UseQueryOptions<any, any, any, QueryKeyArray>> & {
    computed_fields?: any;
  }
) => {
  const queryKey: QueryKeyArray = useMemo(
    () => [
      endpoint,
      options,
      queryOptions?.computed_fields
        ? { computed_fields: queryOptions.computed_fields }
        : undefined
    ],
    [endpoint, options, queryOptions?.computed_fields]
  );
  const { isAuthenticated, token, tenantId, tokenExpiry, login } = useAuth();

  const authRef = useRef({ token, tenantId, tokenExpiry });
  authRef.current = { token, tenantId, tokenExpiry };

  return useQuery({
    queryKey,
    queryFn: async ({ queryKey }: QueryFunctionContext<QueryKeyArray>) =>
      useAPIHelper(authRef.current, queryKey),
    ...queryOptions,
    // After the caller's options, so their `enabled` narrows the sign-in check rather than replacing it.
    enabled: isAuthenticated && (queryOptions?.enabled ?? true),
    retry: createAuthRetryHandler(login)
  });
};

export const useResetAPI = (
  endpoint: string,
  reset: boolean = true,
  pageName?: string,
  entityId?: string
) => {
  const queryClient = useQueryClient();

  return () => {
    const queryKey: QueryKeyArray =
      pageName && entityId
        ? [`${pageName}/${endpoint}`, { pathParams: { entityId } }]
        : [endpoint];

    queryClient.invalidateQueries({ queryKey: queryKey });
    if (reset) {
      queryClient.resetQueries(
        {
          queryKey: queryKey,
          exact: false,
          type: "all"
        },
        {
          throwOnError: false,
          cancelRefetch: true
        }
      );
    }
  };
};

export const useMultipleAPIs = (
  configs: APIConfig[]
): UseQueryResult<unknown, Error>[] => {
  const { isAuthenticated, token, tenantId, tokenExpiry, login } = useAuth();

  const authRef = useRef({ token, tenantId, tokenExpiry });
  authRef.current = { token, tenantId, tokenExpiry };

  const queryConfigs = useMemo(
    () =>
      configs.map(({ endpoint, options, queryOptions }) => {
        const queryKey: QueryKeyArray = [
          endpoint,
          options,
          queryOptions?.computed_fields
            ? { computed_fields: queryOptions.computed_fields }
            : undefined
        ];

        return {
          queryKey,
          queryFn: async ({ queryKey }: QueryFunctionContext<QueryKeyArray>) =>
            useAPIHelper(authRef.current, queryKey),
          ...queryOptions,
          enabled: isAuthenticated && (queryOptions?.enabled ?? true),
          retry: createAuthRetryHandler(login)
        } as UseQueryOptions<unknown, Error, unknown, QueryKeyArray>;
      }),
    [configs, isAuthenticated, login]
  );

  return useQueries({
    queries: queryConfigs
  });
};

type SimpleMutationData = Record<string, any>;
type AdvancedMutationData = {
  data?: any;
  pathParams?: Record<string, string>;
};

// Lifecycle hooks for callers that patch the cache before the server answers.
export type APIMutationLifecycle<TResponse, TRequest, TContext> = Pick<
  UseMutationOptions<TResponse, unknown, TRequest, TContext>,
  "mutationKey" | "onMutate" | "onError" | "onSettled"
>;

export const useAPIMutation = <
  TRequest extends SimpleMutationData | AdvancedMutationData,
  TResponse,
  TContext = unknown
>(
  endpoint: string,
  options: UseAPIOptions = {},
  onSuccessFn?: (data: TResponse, variables: TRequest) => void,
  onErrorFn?: (data: TResponse, variables: TRequest) => void,
  lifecycle?: APIMutationLifecycle<TResponse, TRequest, TContext>
) => {
  const { token, tenantId, tokenExpiry, login } = useAuth();

  const authRef = useRef({ token, tenantId, tokenExpiry });
  authRef.current = { token, tenantId, tokenExpiry };

  const mutationKey: QueryKeyArray = useMemo(
    () => [endpoint, { ...options, method: options.method || "POST" }],
    [endpoint, options]
  );

  return useMutation<TResponse, any, TRequest, TContext>({
    mutationFn: async (data) => {
      try {
        // Handle pathParams for all methods
        if ("pathParams" in data) {
          mutationKey[1]!.pathParams = {
            ...(data.pathParams as Record<string, string>)
          };
        }

        // Handle queryParams
        if ("queryParams" in data) {
          mutationKey[1]!.queryParams = {
            ...(data.queryParams as Record<string, string>)
          };
        }

        // Handle data assignment more similarly to Option 1
        if ("data" in data && data.data !== undefined) {
          mutationKey[1]!.data = data.data;
        } else if (Object.keys(data).length > 0) {
          mutationKey[1]!.data = data;
        }

        return await useAPIHelper(authRef.current, mutationKey);
      } catch (error: any) {
        if (
          !isHandledLinkedAccountRefusal(error) &&
          (error?.message === TOKEN_EXPIRED_ERROR ||
            error?.message === UNAUTHORIZED_ERROR)
        ) {
          requestLogin(login);
        }
        throw error;
      }
    },
    onSuccess: onSuccessFn,
    ...lifecycle,
    // Both run: the lifecycle undoes its cache patch, the caller reacts.
    onError: (error, variables, context) => {
      lifecycle?.onError?.(error, variables, context);
      onErrorFn?.(error, variables);
    }
  });
};

export const useDistinctFilterOptions = (
  pageName: string,
  fieldName: string,
  filters: Filters = EmptyFilters,
  searchTerm?: string
) => {
  const result = useAPI(`${pageName}/distinctValues`, {
    method: "POST",
    pathParams: {
      fieldName
    },
    data: {
      filters: collapseNoValueFilters(filters),
      aggregations: {},
      sort: {},
      search: searchTerm ? { term: searchTerm } : {}
    }
  });
  return result;
};

export default useAPI;
